import { Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import Button from "components/button";
import Input, { Textarea } from "components/input";
import Select from "components/select";
import Spinner from "components/spinner";
import Typography from "components/typography";
import ConfirmationModal from "components/shared/confirmation-modal";
import useEditDistanceTypeForm from "./useEditDistanceTypeForm";

export const editDistanceTypePageRouteName = (id?: string) =>
  `/config/distance-type/${id ?? ":id"}/edit`;

const EditDistanceTypePage = () => {
  const { id } = useParams<{ id: string }>();
  const {
    handleSubmit,
    errors,
    register,
    control,
    distanceCondition,
    showSaveModal,
    setShowSaveModal,
    showCancelModal,
    setShowCancelModal,
    onSubmit,
    handleConfirmSave,
    handleConfirmCancel,
    isLoading,
    t,
  } = useEditDistanceTypeForm(id);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-2xl border border-border bg-white p-6 shadow-sm">
        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col gap-4"
        >
          {/* Row: Name + Distance */}
          <div className="grid grid-cols-2 gap-4">
            {/* Distance Type Name */}
            <div className="flex flex-col gap-1.5">
              <Typography variant="bodyLarge">
                {t("label-distance-type-name")}{" "}
                <span className="text-error-500">*</span>
              </Typography>
              <Input
                placeholder={t("label-distance-type-name")}
                required
                {...register("distanceTypeName")}
                error={errors.distanceTypeName}
                data-testid="input-distance-type-name-edit"
              />
            </div>

            {/* Distance (Km) */}
            <div className="flex flex-col gap-1.5">
              <Typography variant="bodyLarge">
                {t("label-distance")}{" "}
                <span className="text-error-500">*</span>
              </Typography>
              <div className="flex items-start gap-2">
                <div className="w-[140px] shrink-0">
                  <Controller
                    name="distanceCondition"
                    control={control}
                    render={({ field }) => (
                      <Select
                        options={[
                          { label: t("condition-less-than"), value: "below" },
                          { label: t("condition-between"), value: "between" },
                          { label: t("condition-more-than"), value: "above" },
                        ]}
                        value={field.value}
                        onValueChange={(val) => field.onChange(val)}
                        data-testid="dropdown-distance-condition-edit"
                      />
                    )}
                  />
                </div>

                {distanceCondition === "between" ? (
                  <div className="flex items-start gap-2 flex-1">
                    <Input
                      type="number"
                      enableNumberFormat
                      placeholder="0"
                      {...register("distanceMin")}
                      error={errors.distanceMin}
                      data-testid="input-distance-min-value-edit"
                    />
                    <div className="flex items-center gap-2 shrink-0 pt-2.5">
                      <span className="text-xs font-medium text-white bg-primary-500 rounded px-2 py-0.5">{t("label-distance-min")}</span>
                    </div>
                    <Input
                      type="number"
                      enableNumberFormat
                      placeholder="0"
                      {...register("distanceMax")}
                      error={errors.distanceMax}
                      data-testid="input-distance-max-value-edit"
                    />
                    <div className="flex items-center gap-2 shrink-0 pt-2.5">
                      <span className="text-xs font-medium text-white bg-primary-500 rounded px-2 py-0.5">{t("label-distance-max")}</span>
                    </div>
                  </div>
                ) : (
                  <div className="flex-1">
                    <Input
                      type="number"
                      enableNumberFormat
                      placeholder="0"
                      {...register("distanceValue")}
                      error={errors.distanceValue}
                      data-testid={distanceCondition === "below" ? "input-distance-max-value-edit" : "input-distance-min-value-edit"}
                    />
                  </div>
                )}
              </div>
              <Typography variant="body" tone="muted" className="text-xs">
                {distanceCondition === "below" && t("hint-less-than")}
                {distanceCondition === "between" && t("hint-between")}
                {distanceCondition === "above" && t("hint-more-than")}
              </Typography>
            </div>
          </div>

          {/* Description */}
          <div className="flex flex-col gap-1.5">
            <Typography variant="bodyLarge">
              {t("label-description")}
            </Typography>
            <Textarea
              placeholder={t("label-description")}
              {...register("description")}
              error={errors.description}
              data-testid="textarea-distance-description-edit"
            />
          </div>
        </form>
      </section>

      {/* Bottom Action Bar */}
      <div className="flex justify-end gap-3">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          size="medium"
          onClick={() => setShowCancelModal(true)}
          data-testid="btn-cancel-distance-edit"
        >
          {t("btn-cancel")}
        </Button>
        <Button
          type="button"
          variant="fill"
          color="primary"
          size="medium"
          onClick={handleSubmit(onSubmit)}
          data-testid="btn-save-distance-edit"
        >
          {t("btn-save")}
        </Button>
      </div>

      {/* Save Confirmation Modal */}
      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title")}
        message={t("modal-save-message")}
        confirmLabel={t("modal-confirm-save")}
        cancelLabel={t("modal-confirm-cancel")}
        onConfirm={handleConfirmSave}
        onCancel={() => setShowSaveModal(false)}
        testId="save-distance-type-edit-modal"
      />

      {/* Discard Confirmation Modal */}
      <ConfirmationModal
        open={showCancelModal}
        title={t("modal-discard-title")}
        message={t("modal-discard-message")}
        confirmLabel={t("modal-confirm-discard")}
        cancelLabel={t("modal-confirm-stay")}
        onConfirm={handleConfirmCancel}
        onCancel={() => setShowCancelModal(false)}
        testId="discard-distance-type-edit-modal"
      />
    </div>
  );
};

export default EditDistanceTypePage;
